// What a page's tick also allows, as the small grey lines of Access Management → Web Access.
//
// Since 14 Oct 2026 a page's tick gives every right that belongs to it (the owner's "page
// tick gives every right"): Exit Reviews can start an exit, Stores approves store requests
// and links hiring, and so on. The server holds the one map (apps/api/src/services/
// page-rights.ts) and hands it out at GET /api/menus/page-rights; this only lays it over
// the menu rows, so whoever ticks a page can see what they are giving.
//
// One row is not a page: "Raise & cancel hiring" (the product owner, 14 Oct 2026: a row of
// its own to decide who raises hiring). Its tick IS the right, so its line is the right's
// own `rowNote` - what it allows and who it is for - with no "also:".

export interface PageRightPage {
  name: string;
  /** menus.code, or null where no code can be relied on. */
  code: string | null;
  /** menus.path values that are this page. */
  paths: string[];
  /** A row of Access Management that is not a page: its tick is the right itself. */
  rowOnly?: boolean;
}

export interface PageRightEntry {
  key: string;
  label: string;
  /** A few words: what the page's tick also allows. */
  also: string;
  /** For a row that is not a page: its whole line, said without "also:". */
  rowNote?: string;
  pages: PageRightPage[];
  /**
   * Menu codes whose tick does NOT carry this right although a page of it matches them by
   * address: the Dashboard tab "Hiring" (code dashboard_hiring) shares /dashboard with every
   * tab, and its tick carries the Hiring rights only - not Custom Dashboards.
   */
  exceptCodes?: string[];
}

export interface MenuRowLike {
  id: string;
  code: string;
  path?: string | null;
  parent_id: string | null;
}

export interface PageRightNotes {
  /** menu id -> "also: …" for that row. */
  row: Map<string, string>;
  /** parent menu id -> "any tick here also: …" for a group whose header row itself carries a right. */
  group: Map<string, string>;
}

const matches = (page: PageRightPage, menu: MenuRowLike): boolean =>
  (page.code !== null && page.code !== '' && page.code === menu.code)
  || (!!menu.path && page.paths.includes(menu.path));

/** Does the right leave this row out by its code (the Hiring tab, for Custom Dashboards)? */
const leavesOut = (right: PageRightEntry, menu: MenuRowLike): boolean => (right.exceptCodes ?? []).includes(menu.code);

const isPageOf = (right: PageRightEntry, menu: MenuRowLike): boolean => !leavesOut(right, menu) && right.pages.some((page) => matches(page, menu));

/** Is this menu row a row that is not a page, for this right (found by its code)? */
const isRowOnlyOf = (right: PageRightEntry, menu: MenuRowLike): boolean =>
  right.pages.some((page) => page.rowOnly === true && matches(page, menu));

const phrases = (rights: PageRightEntry[]): string =>
  [...new Set(rights.map((r) => r.also))].join(' · ');

/**
 * The line under one row: for a row that is not a page, its right's own note (no "also:");
 * otherwise "also: " and what each right the tick carries allows.
 */
const lineOf = (menu: MenuRowLike, carried: PageRightEntry[]): string | null => {
  if (carried.length === 0) return null;
  const own = carried.filter((r) => isRowOnlyOf(r, menu));
  const others = carried.filter((r) => !own.includes(r));
  const parts = [
    ...own.map((r) => r.rowNote || r.also),
    ...(others.length > 0 ? [`also: ${phrases(others)}`] : []),
  ];
  return parts.join(' · ');
};

/**
 * The notes for a menu tree. A right that the group's own header row carries (the
 * Dashboard row, for custom dashboards — every tab is that page too) is said once, on the
 * header, instead of under each row; every other right is said under the row that carries
 * it. A row whose tick carries nothing gets no line.
 */
export const pageRightNotes = (menus: MenuRowLike[], rights: PageRightEntry[]): PageRightNotes => {
  const row = new Map<string, string>();
  const group = new Map<string, string>();
  const byParent = new Map<string, MenuRowLike[]>();
  for (const menu of menus) {
    if (menu.parent_id) byParent.set(menu.parent_id, [...(byParent.get(menu.parent_id) ?? []), menu]);
  }
  for (const menu of menus.filter((m) => !m.parent_id)) {
    const children = byParent.get(menu.id) ?? [];
    const own = rights.filter((r) => isPageOf(r, menu));
    if (children.length === 0) {
      const line = lineOf(menu, own);
      if (line) row.set(menu.id, line);
      continue;
    }
    // Said once on the header only for a right EVERY row under it carries; one some row leaves
    // out by its code (Custom Dashboards, not the Hiring tab) is said under each row that has it.
    const forAll = own.filter((r) => !children.some((child) => leavesOut(r, child)));
    if (forAll.length > 0) group.set(menu.id, `any tick here also: ${phrases(forAll)}`);
    const saidOnHeader = new Set(forAll.map((r) => r.key));
    for (const child of children) {
      const line = lineOf(child, rights.filter((r) => !saidOnHeader.has(r.key) && isPageOf(r, child)));
      if (line) row.set(child.id, line);
    }
  }
  return { row, group };
};
